/**
 * The **health globe** - the client face of one dungeon `dungeonGlobe` entity
 * (PRD #794; sim half in `src/sim/dungeonCombat.ts`).
 *
 * Sustain on a dense floor has to read at a glance and from across a room, so a
 * globe is a small red orb with a soft halo that bobs and pulses in place. It is
 * deliberately not clickable and carries no label: it is collected by walking
 * over it, and anything that invited a click would be lying about that.
 *
 * All motion is deterministic from the entity id, like the arena's XP orbs, so
 * re-rendering the same snapshot draws the same globe and the sprite unit-tests
 * headlessly.
 */
import { Container, Graphics } from 'pixi.js';

/** Radius of the globe body, px. */
const GLOBE_RADIUS_PX = 4.2;

/** Radius of the soft halo behind it, px. */
const HALO_RADIUS_PX = 7.5;

/** Bob period and amplitude: alive, but never mistaken for a mobile. */
const BOB_PERIOD_MS = 1600;
const BOB_AMPLITUDE_PX = 1.8;

/** Pulse period of the halo's alpha. */
const PULSE_PERIOD_MS = 1100;

const GLOBE_COLOR = 0xe23b3b;
const HALO_COLOR = 0xff7a7a;

export class HealthGlobeSprite extends Container {
  private readonly halo = new Graphics();
  private readonly body = new Graphics();
  /** Deterministic per-entity phase, so a field of globes does not throb in unison. */
  private readonly phase: number;
  private elapsedMs = 0;

  constructor(seed: number) {
    super();
    this.phase = ((Math.imul(seed + 1, 0x9e3779b1) >>> 0) % 1000) / 1000;
    this.halo.circle(0, 0, HALO_RADIUS_PX).fill({ color: HALO_COLOR, alpha: 0.35 });
    this.body.circle(0, 0, GLOBE_RADIUS_PX).fill({ color: GLOBE_COLOR, alpha: 0.95 });
    this.body
      .circle(-GLOBE_RADIUS_PX * 0.3, -GLOBE_RADIUS_PX * 0.35, GLOBE_RADIUS_PX * 0.3)
      .fill({ color: 0xffffff, alpha: 0.5 });
    this.addChild(this.halo);
    this.addChild(this.body);
    this.update(0);
  }

  /** Advance the idle motion by one rendered frame. */
  update(frameDtMs: number): void {
    this.elapsedMs += Math.max(0, frameDtMs);
    const bob = Math.sin(
      ((this.elapsedMs / BOB_PERIOD_MS) + this.phase) * Math.PI * 2,
    );
    const pulse = Math.sin(
      ((this.elapsedMs / PULSE_PERIOD_MS) + this.phase) * Math.PI * 2,
    );
    this.body.y = bob * BOB_AMPLITUDE_PX;
    this.halo.y = this.body.y;
    this.halo.alpha = 0.75 + pulse * 0.25;
    this.halo.scale.set(0.9 + pulse * 0.1);
  }

  /** A globe always animates, so a scene holding one is never idle. */
  get requiresActivePresentation(): boolean {
    return true;
  }

  dispose(): void {
    this.destroy({ children: true });
  }
}
